import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { activateWaitingWorker, registerPwa } from "@/pwa/client";
import { updateAppBadge } from "@/pwa/badge";
import { SocketContext } from "@/context";
import { subscribeToPush } from "@/pwa/push";
import { getVapidPublicKey, savePushSubscription } from "@/services/pwa";
import { useAppSelector } from "@/redux/hooks";

type PwaStatusProps = {
  online?: boolean;
  waitingWorker?: ServiceWorker;
};

export default function PwaStatus({ online, waitingWorker }: PwaStatusProps) {
  const [isOnline, setIsOnline] = useState(online ?? true);
  const [waiting, setWaiting] = useState(waitingWorker);
  const socketContext = useContext(SocketContext);
  const isLoggedIn = useAppSelector((state) => state.user.isLoggedIn);
  const hasRequestedPush = useRef(false);
  const unreadChatCount =
    (socketContext?.isUnreadMsgs?.length ?? 0) +
    (socketContext?.familyUnreadCount ?? 0);

  useEffect(() => {
    updateAppBadge(unreadChatCount);
  }, [unreadChatCount]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (online === undefined) setIsOnline(navigator.onLine);
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    void registerPwa(setWaiting).catch(() => undefined);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [online]);

  const subscribeAuthenticatedUser = useCallback(async () => {
    const configuration = await getVapidPublicKey();
    if (
      !configuration?.success ||
      !configuration.configured ||
      !configuration.publicKey
    ) {
      return;
    }
    await subscribeToPush({
      vapidPublicKey: configuration.publicKey,
      createSubscription: async (subscription) => {
        const response = await savePushSubscription(subscription);
        if (!response?.success) {
          throw new Error(
            response?.message || "Unable to save push subscription"
          );
        }
      },
    });
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      hasRequestedPush.current = false;
      return;
    }
    if (
      hasRequestedPush.current ||
      typeof Notification === "undefined" ||
      Notification.permission === "denied"
    ) {
      return;
    }

    hasRequestedPush.current = true;
    void subscribeAuthenticatedUser().catch(() => undefined);
  }, [isLoggedIn, subscribeAuthenticatedUser]);

  if (!isOnline) {
    return (
      <div role="status">
        You are offline. Reconnect to access current care information.
      </div>
    );
  }
  if (!waiting) return null;
  return (
    <div role="status">
      <span>An update is ready. </span>
      <button onClick={() => activateWaitingWorker(waiting)}>Reload</button>
    </div>
  );
}
